import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash, faStop, faPlay, faPause, faPaperPlane, faCircle
} from '@fortawesome/free-solid-svg-icons';

import { useAudioRecorder, voiceNoteFile } from '../../hooks/useAudioRecorder';
import '../../styles/audio.css';

const fmt = (ms) => {
    const total = Math.floor(ms / 1000);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const BAR_STEP = 5;   // px per bar: a 3px bar and a 2px gap, as a posted note draws

/** Average `peaks` down (or repeat them up) to exactly `count` values. */
const resample = (peaks, count) => {
    if (!count) return [];
    if (!peaks.length) return Array(count).fill(0.15);
    return Array.from({ length: count }, (_, i) => {
        const from = Math.floor((i * peaks.length) / count);
        const to = Math.max(from + 1, Math.floor(((i + 1) * peaks.length) / count));
        const slice = peaks.slice(from, to);
        return slice.reduce((a, b) => a + b, 0) / slice.length;
    });
};

/**
 * The recorded note across the full width of the row. Bars keep their posted
 * size and a wider row simply gets more of them — scaling a fixed set of bars
 * to fit is what made the SVG waveform look stretched here.
 */
const FillWaveform = ({ peaks, progress, onSeek }) => {
    const ref = useRef(null);
    const [count, setCount] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const measure = () => setCount(Math.max(1, Math.floor((el.clientWidth + 2) / BAR_STEP)));
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const bars = useMemo(() => resample(peaks, count), [peaks, count]);

    return (
        <div
            ref={ref}
            className="vnb-wave"
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                if (rect.width) onSeek(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
            }}
        >
            {bars.map((p, i) => (
                <span
                    key={i}
                    className={(i + 1) / bars.length <= progress ? 'is-played' : undefined}
                    style={{ height: `${Math.max(8, Math.min(1, p) * 100)}%` }}
                />
            ))}
        </div>
    );
};

/**
 * The chat composer while a voice note is being recorded, WhatsApp-style: the
 * text box and paperclip are gone, and the row is discard · live waves · send.
 *
 * Send works straight from recording (stop and send in one tap). Stop is there
 * for when you want to hear it back first; the row then becomes a plain player
 * — play, the waves across the full row, the length — with the same discard
 * and send either side.
 *
 * `onSend(file, meta)` is awaited; the bar closes once it resolves. A rejected
 * send leaves the take in place so it can be retried rather than lost.
 */
const VoiceNoteBar = ({ onSend, onClose }) => {
    const {
        isSupported, status, error, recordingTimeMs, liveLevels,
        recordedBlob, waveform,
        startRecording, stopRecording, discardRecording
    } = useAudioRecorder();

    const [sending, setSending] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const audioRef = useRef(null);
    const startedRef = useRef(false);

    useEffect(() => {
        if (!startedRef.current) {
            startedRef.current = true;
            startRecording();
        }
    }, [startRecording]);

    // Mic refused or unavailable: nothing to show but the reason, so hand the
    // row back once it has been read.
    useEffect(() => {
        if (!isSupported) onClose?.();
    }, [isSupported, onClose]);

    const previewUrl = useMemo(
        () => (recordedBlob ? URL.createObjectURL(recordedBlob) : null),
        [recordedBlob]
    );
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    const discard = () => {
        discardRecording();
        onClose?.();
    };

    const send = async () => {
        if (sending) return;
        setSending(true);
        try {
            const take = status === 'preview'
                ? { blob: recordedBlob, waveform, durationMs: recordingTimeMs }
                : await stopRecording();
            if (!take?.blob?.size) return;
            await onSend(voiceNoteFile(take.blob), {
                waveform: take.waveform,
                durationMs: take.durationMs
            });
            discardRecording();
            onClose?.();
        } catch {
            // The composer reports the failure; the take stays for a retry.
        } finally {
            setSending(false);
        }
    };

    const togglePlay = () => {
        const el = audioRef.current;
        if (!el) return;
        if (el.paused) { el.play().catch(() => { }); setPlaying(true); }
        else { el.pause(); setPlaying(false); }
    };

    const live = status === 'recording' || status === 'paused';

    return (
        <div className="vnb">
            <button type="button" className="vnb-btn is-discard" onClick={discard}
                disabled={sending} title="Discard" aria-label="Discard voice note">
                <FontAwesomeIcon icon={faTrash} />
            </button>

            {status === 'preview' ? (
                <div className="vnb-preview">
                    <button type="button" className="vnb-play" onClick={togglePlay}
                        aria-label={playing ? 'Pause' : 'Play back'}>
                        <FontAwesomeIcon icon={playing ? faPause : faPlay} />
                    </button>
                    <FillWaveform
                        peaks={waveform}
                        progress={progress}
                        onSeek={(p) => {
                            const el = audioRef.current;
                            if (el && el.duration && isFinite(el.duration)) {
                                el.currentTime = p * el.duration;
                                setProgress(p);
                            }
                        }}
                    />
                    <span className="vnb-time">{fmt(recordingTimeMs)}</span>
                    <audio
                        ref={audioRef}
                        src={previewUrl}
                        onTimeUpdate={(e) => {
                            const el = e.currentTarget;
                            if (el.duration && isFinite(el.duration)) setProgress(el.currentTime / el.duration);
                        }}
                        onEnded={() => { setPlaying(false); setProgress(0); }}
                        hidden
                    />
                </div>
            ) : (
                <div className="vnb-track">
                    {status === 'requesting' && (
                        <span className="vnb-hint">Waiting for microphone permission…</span>
                    )}

                    {status === 'idle' && error && <span className="vnb-hint is-error">{error}</span>}

                    {live && (
                        <>
                            <span className="vnb-dot"><FontAwesomeIcon icon={faCircle} /></span>
                            <span className="vnb-time">{fmt(recordingTimeMs)}</span>
                            <div className="vnb-bars" aria-hidden="true">
                                {(liveLevels.length ? liveLevels : [0]).map((l, i) => (
                                    <span key={i} style={{ height: `${Math.max(10, l * 100)}%` }} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {live && (
                <button type="button" className="vnb-btn is-stop" onClick={() => stopRecording()}
                    disabled={sending} title="Stop and listen" aria-label="Stop recording">
                    <FontAwesomeIcon icon={faStop} />
                </button>
            )}

            <button
                type="button"
                className="msgr-send"
                onClick={send}
                disabled={sending || !(live || status === 'preview')}
                title="Send voice note"
            >
                <FontAwesomeIcon icon={faPaperPlane} />
            </button>
        </div>
    );
};

export default VoiceNoteBar;
